import { motion } from "framer-motion";
import { Sparkles, ShieldAlert, Slack, Lock, Check, AlertTriangle } from "lucide-react";
import { useHive } from "@/store/useHive";
import { APPS } from "@/data/apps";
import { AUTO_RESOLVE_LEVELS, AUTO_RESOLVE_GUARDRAILS } from "@/data/tasks";

const accent = APPS.tasks.accent;

/** The auto-resolve autonomy slider with guardrails and a Slack dependency warning. */
export function AutoResolvePanel() {
  const level = useHive((s) => s.autoResolveLevel);
  const setLevel = useHive((s) => s.setAutoResolveLevel);
  const slackConnected = useHive((s) =>
    s.integrations.some((i) => i.id === "slack" && i.status === "connected")
  );
  const cur = AUTO_RESOLVE_LEVELS[level];
  const needsSlack = level >= 3;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 rounded-xl border border-app bg-surface-2/40 p-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl text-black" style={{ background: accent }}>
          <Sparkles size={18} />
        </span>
        <div className="flex-1">
          <div className="text-sm font-semibold text-app">Level {level} · {cur.name}</div>
          <div className="text-xs text-app-2">{cur.desc}</div>
        </div>
      </div>

      {/* slider */}
      <div className="px-1">
        <div className="relative h-2 rounded-full bg-surface-2">
          <motion.div
            className="absolute left-0 top-0 h-2 rounded-full"
            style={{ background: accent }}
            animate={{ width: `${(level / 4) * 100}%` }}
            transition={{ type: "spring", stiffness: 320, damping: 30 }}
          />
          {AUTO_RESOLVE_LEVELS.map((l) => (
            <button
              key={l.level}
              onClick={() => setLevel(l.level)}
              className="absolute top-1/2 grid h-6 w-6 -translate-x-1/2 -translate-y-1/2 place-items-center"
              style={{ left: `${(l.level / 4) * 100}%` }}
              title={l.name}
            >
              <span
                className="h-3.5 w-3.5 rounded-full border-2 transition"
                style={{
                  background: l.level <= level ? accent : "hsl(var(--surface))",
                  borderColor: l.level <= level ? accent : "hsl(var(--border-strong))",
                  transform: l.level === level ? "scale(1.4)" : "scale(1)",
                }}
              />
            </button>
          ))}
        </div>
        <div className="mt-2 flex justify-between">
          {AUTO_RESOLVE_LEVELS.map((l) => (
            <button
              key={l.level}
              onClick={() => setLevel(l.level)}
              className="w-[18%] text-center text-[9px] font-medium leading-tight transition"
              style={{ color: l.level === level ? accent : "var(--text-2)" }}
            >
              {l.short}
            </button>
          ))}
        </div>
      </div>

      {/* Slack dependency warning (cross-widget) */}
      {needsSlack && !slackConnected && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3">
          <Slack size={15} className="mt-0.5 text-amber-500" />
          <div className="text-xs text-amber-600 dark:text-amber-400">
            <span className="font-medium">Slack is required</span> to automatically ask follow-up questions at this level. Connect Slack in the Integration Center.
          </div>
        </div>
      )}
      {needsSlack && slackConnected && (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-2.5 text-xs text-emerald-600 dark:text-emerald-400">
          <Check size={14} /> Slack is connected — follow-up questions can be sent automatically.
        </div>
      )}

      {/* Guardrails */}
      {level >= 2 && (
        <div>
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-app-2">
            <ShieldAlert size={12} /> Guardrails
          </div>
          <div className="space-y-1.5">
            {AUTO_RESOLVE_GUARDRAILS.map((g) => (
              <div key={g} className="flex items-center gap-2 rounded-lg border border-app bg-surface-2/30 px-3 py-2 text-xs text-app-1">
                {g.includes("Slack") ? (
                  <Slack size={13} className="text-app-2" />
                ) : g.includes("admin") ? (
                  <Lock size={13} className="text-app-2" />
                ) : g.includes("ARES") ? (
                  <Check size={13} className="text-app-2" />
                ) : (
                  <AlertTriangle size={13} className="text-app-2" />
                )}
                {g}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
